import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Show, SignIn, SignUp, useAuth, UserButton } from "@clerk/react";
import { accountRoutes, socialLinks } from "@dropdex/config";
import { Badge, BrandLogo, Button, Panel } from "@dropdex/ui";

const logo = "/images/brand/dropdex-primary.png";
const apiBaseUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, "") || "https://app.dropdexalerts.app";

const authRoutes: Record<string, { title: string; copy: string; action: string }> = {
  "/sign-up": { title: "Create your DropDex account", copy: "One account for the mobile and desktop apps.", action: "Create account" },
  "/sign-in": { title: "Welcome back", copy: "Sign in to continue to your account.", action: "Sign in" },
  "/verify-email": { title: "Verify your email", copy: "Enter the six-digit code sent to your inbox.", action: "Verify email" },
  "/forgot-password": { title: "Reset your password", copy: "We’ll send a secure reset link to your email.", action: "Send reset link" },
  "/reset-password": { title: "Choose a new password", copy: "Use at least 12 characters for better security.", action: "Update password" },
  "/auth/callback": { title: "Finishing sign in", copy: "Securely connecting your DropDex account.", action: "Continue" }
};

const clerkConfigured = Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);
const clerkAppearance = {
  variables: {
    colorPrimary: "#F5C400",
    colorBackground: "#070a20",
    colorText: "#ffffff",
    colorTextSecondary: "#9ca5b8",
    colorInputBackground: "#0c1230",
    colorInputText: "#ffffff",
    borderRadius: "0.8rem"
  },
  elements: {
    cardBox: "clerk-card-box",
    card: "clerk-card",
    footerActionLink: "clerk-link",
    formButtonPrimary: "clerk-primary-button"
  }
};

const purchaseRoutes = ["/checkout", "/checkout/success", "/checkout/canceled", "/download", "/download/windows", "/download/macos"];
const legalRoutes = ["/privacy", "/terms", "/refund-policy", "/support"];

function requestedRedirectUrl(fallback: string) {
  const requested = new URLSearchParams(window.location.search).get("redirect_url");
  return requested?.startsWith("/") && !requested.startsWith("//") ? requested : fallback;
}

function authUrl(path: "/sign-in" | "/sign-up", redirectUrl: string) {
  return `${path}?redirect_url=${encodeURIComponent(redirectUrl)}`;
}

function SiteHeader() {
  return (
    <header className="site-header">
      <BrandLogo src={logo} />
      <nav aria-label="Main navigation">
        <a href="/">Home</a>
        {clerkConfigured ? <>
          <Show when="signed-out"><a href="/">Membership</a></Show>
          <Show when="signed-in"><SignedInMembershipLink /></Show>
        </> : <a href="/">Membership</a>}
      </nav>
      <div className="header-actions">
        {clerkConfigured ? <>
          <Show when="signed-out"><a href="/sign-in">Sign in</a><a className="mini-cta" href="/checkout">Start free trial</a></Show>
          <Show when="signed-in"><a href="/account/billing">Account</a><UserButton /></Show>
        </> : <><a href="/sign-in">Sign in</a><a className="mini-cta" href="/checkout">Start free trial</a></>}
      </div>
    </header>
  );
}

function SignedInMembershipLink() {
  const { billing, state } = useBillingStatus();
  if (state !== "ready" || billing?.effectiveAccess) return null;
  return <a href="/">Membership</a>;
}

function SocialIcon({ label }: { label: string }) {
  if (label === "X") {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4.5 19 19.5M19 4.5 5 19.5" /></svg>;
  }
  if (label === "TikTok") {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 4v10.5a4.5 4.5 0 1 1-3-4.24V13a2 2 0 1 0 1 1.73V4h2Zm0 0c.55 2.2 2.05 3.7 4.25 4.25" /></svg>;
  }
  return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="4" /><circle cx="12" cy="12" r="3.5" /><circle cx="17.2" cy="6.8" r=".8" className="social-icon-fill" /></svg>;
}

function SimpleFooter() {
  return (
    <footer className="simple-footer">
      <nav aria-label="Legal links"><a href="/terms">Terms</a><a href="/privacy">Privacy Policy</a><a href="/refund-policy">Refund Policy</a></nav>
      <div className="simple-socials" aria-label="DropDex social media">
        {socialLinks.map((social) => <a key={social.href} href={social.href} target="_blank" rel="noreferrer" aria-label={social.label}><SocialIcon label={social.label} /></a>)}
      </div>
    </footer>
  );
}

function LandingPage() {
  const apiUrl = apiBaseUrl;
  const [activeMembers, setActiveMembers] = useState<number | null>(null);
  const [memberLimit, setMemberLimit] = useState(2300);

  useEffect(() => {
    let active = true;

    async function loadActiveMembers() {
      if (!apiUrl) return;
      try {
        const response = await fetch(`${apiUrl}/api/v1/membership/active-count`, { cache: "no-store" });
        if (!response.ok) throw new Error("Unable to load the active membership count.");
        const payload = await response.json() as { data?: { activeMembers?: number; memberLimit?: number } };
        const count = payload.data?.activeMembers;
        if (active && typeof count === "number" && Number.isFinite(count)) {
          setActiveMembers(Math.max(0, Math.trunc(count)));
          const limit = payload.data?.memberLimit;
          if (typeof limit === "number" && Number.isFinite(limit) && limit > 0) {
            setMemberLimit(Math.trunc(limit));
          }
        }
      } catch {
        if (active) setActiveMembers(null);
      }
    }

    void loadActiveMembers();
    const refresh = window.setInterval(() => void loadActiveMembers(), 30_000);
    return () => {
      active = false;
      window.clearInterval(refresh);
    };
  }, [apiUrl]);

  return (
    <div className="simple-landing-page">
      <header className="simple-header">
        <a className="simple-brand" href="/" aria-label="DropDex Alerts home">
          <img src={logo} alt="" />
          <span>DropDex Alerts</span>
        </a>
        {clerkConfigured
          ? <><Show when="signed-out"><a href="/sign-in">Login</a></Show><Show when="signed-in"><a href="/account/billing">Account</a></Show></>
          : <a href="/sign-in">Login</a>}
      </header>
      <main className="simple-landing">
        <section className="simple-hero">
          <h1>Stay Ahead of Every TCG Drop.</h1>
          <p>Join our Discord community now.</p>

          <div className="member-count" aria-live="polite" aria-busy={activeMembers === null}>
            <span className="live-label"><i aria-hidden="true" /> Live membership</span>
            <div className="member-total">
              <strong>{activeMembers === null ? "—" : activeMembers.toLocaleString()}</strong>
              <span>/ {memberLimit.toLocaleString()}</span>
            </div>
            <p className="member-title">Active Members</p>
            <p className="member-limit-copy">Limited to ensure all Discord push notifications are delivered successfully.</p>
          </div>

          <div className="simple-features">
            <h2>Everything You Need for TCG Drops</h2>
            <ul>
              {["Real-Time TCG Restock Alerts", "New Release & Preorder Alerts", "TCG Deals", "Collector News & Updates", "DropDex Discord Community", "Pokémon, One Piece, Lorcana, & many more!"].map((feature) => (
                <li key={feature}><span aria-hidden="true">✓</span>{feature}</li>
              ))}
            </ul>
          </div>

          <div className="simple-join">
            <a className="button button-gold" href="/checkout">Join Now</a>
            <p>7-Day Free Trial <span aria-hidden="true">•</span> $5/month after trial</p>
            <p className="simple-cancel">Cancel at any time during your free trial in your DropDex account.</p>
          </div>
        </section>
      </main>
      <SimpleFooter />
    </div>
  );
}

function AuthPage({ route }: { route: keyof typeof authRoutes }) {
  const content = authRoutes[route];
  const redirectUrl = requestedRedirectUrl("/account/billing");
  if (!clerkConfigured) {
    return <PageFrame><div className="center-card"><BrandLogo src={logo} /><Badge tone="muted">Configuration required</Badge><h1>{content.title}</h1><p>Add <code>VITE_CLERK_PUBLISHABLE_KEY</code> to the web environment to enable DropDex account access.</p></div></PageFrame>;
  }
  if (route === "/sign-in") {
    return <PageFrame><div className="clerk-auth-shell"><SignIn routing="hash" signUpUrl={authUrl("/sign-up", redirectUrl)} forceRedirectUrl={redirectUrl} fallbackRedirectUrl={redirectUrl} signUpForceRedirectUrl={redirectUrl} signUpFallbackRedirectUrl={redirectUrl} appearance={clerkAppearance} /></div></PageFrame>;
  }
  if (route === "/sign-up") {
    return <PageFrame><div className="clerk-auth-shell"><SignUp routing="hash" signInUrl={authUrl("/sign-in", redirectUrl)} forceRedirectUrl={redirectUrl} fallbackRedirectUrl={redirectUrl} signInForceRedirectUrl={redirectUrl} signInFallbackRedirectUrl={redirectUrl} appearance={clerkAppearance} /></div></PageFrame>;
  }
  return <PageFrame><div className="center-card"><BrandLogo src={logo} /><Badge tone="gold">Secure account flow</Badge><h1>{content.title}</h1><p>Email verification and password recovery are handled securely inside the DropDex sign-in flow.</p><a className="button button-gold" href="/sign-in">Continue to sign in</a></div></PageFrame>;
}

type BillingSummary = {
  plan: { name: string; amountCents: number; currency: string; interval: string; trialDays: number; includesMobile: boolean; includesDesktop: boolean };
  subscription: null | { id: string; status: string; currentPeriodEnd: string | null; trialEnd: string | null; cancelAtPeriodEnd: boolean; amountCents: number; currency: string };
  entitlements: Array<{ key: string; active: boolean; endsAt: string | null }>;
  isEntitled: boolean;
  effectiveAccess: boolean;
  enforcementEnabled: boolean;
  billingConfigured: boolean;
};

function useBillingStatus() {
  const { getToken } = useAuth();
  const [billing, setBilling] = useState<BillingSummary | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const apiUrl = apiBaseUrl;
  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const token = await getToken();
        if (!token || !apiUrl) throw new Error("Missing billing API configuration");
        const response = await fetch(`${apiUrl}/api/v1/billing/subscription`, { headers: { authorization: `Bearer ${token}` } });
        if (!response.ok) throw new Error("Unable to load subscription");
        const payload = await response.json() as { data: BillingSummary };
        if (active) {
          setBilling(payload.data);
          setState("ready");
        }
      } catch {
        if (active) setState("error");
      }
    }
    void load();
    return () => { active = false; };
  }, [apiUrl, getToken]);
  return { billing, state, apiUrl, getToken };
}

async function openBillingDestination(apiUrl: string | undefined, getToken: () => Promise<string | null>, path: "checkout" | "portal") {
  const token = await getToken();
  if (!token || !apiUrl) throw new Error("Missing billing configuration");
  const response = await fetch(`${apiUrl}/api/v1/billing/${path}`, {
    method: "POST",
    headers: { authorization: `Bearer ${token}` }
  });
  const payload = await response.json() as { data?: { url?: string }; error?: { message?: string } };
  if (!response.ok || !payload.data?.url) throw new Error(payload.error?.message || "Unable to open Stripe billing.");
  window.location.assign(payload.data.url);
}

function CheckoutMember() {
  const { billing, state, apiUrl, getToken } = useBillingStatus();
  const [action, setAction] = useState<"ready" | "opening" | "error">("ready");
  const checkoutStarted = useRef(false);
  const alreadyActive = Boolean(billing?.isEntitled && billing.subscription);
  const startCheckout = useCallback(async (destination: "checkout" | "portal" = alreadyActive ? "portal" : "checkout") => {
    try {
      checkoutStarted.current = true;
      setAction("opening");
      await openBillingDestination(apiUrl, getToken, destination);
    } catch {
      checkoutStarted.current = false;
      setAction("error");
    }
  }, [alreadyActive, apiUrl, getToken]);

  useEffect(() => {
    if (state !== "ready" || !billing || alreadyActive || checkoutStarted.current) return;
    void startCheckout("checkout");
  }, [alreadyActive, billing, startCheckout, state]);

  if (alreadyActive) {
    return <div className="center-card wide"><Badge tone="green">Membership active</Badge><h1>Your DropDex membership is active</h1><p>Manage your payment method, receipts, renewal, or cancellation in the secure Stripe billing portal.</p><div className="checkout-total"><span>Current membership</span><strong>$5/month</strong><small>Full Discord access</small></div><Button disabled={action === "opening"} onClick={() => void startCheckout("portal")}>{action === "opening" ? "Opening Stripe…" : "Manage billing"}</Button>{action === "error" && <p className="billing-error">Stripe did not open. Use the button above to try again.</p>}</div>;
  }

  const checkoutFailed = state === "error" || action === "error";
  return <div className="center-card wide checkout-connecting"><h1>{checkoutFailed ? "Checkout needs another try" : "Connecting you to checkout…"}</h1><p>Remember, you can cancel at any time from your account dashboard.</p>{checkoutFailed && <><Button onClick={() => void startCheckout("checkout")}>Try checkout again</Button><p className="billing-error">Your account is saved. Stripe did not open automatically.</p></>}</div>;
}

function CheckoutAccountStep() {
  return <div className="checkout-signup"><div className="checkout-offer"><Badge tone="gold">7-day free trial</Badge><p><strong>$0 today</strong><span>·</span>$5/month after trial</p></div><section className="checkout-auth-panel" aria-label="Sign up for DropDex Alerts"><SignUp routing="hash" signInUrl={authUrl("/sign-in", "/checkout")} forceRedirectUrl="/checkout" fallbackRedirectUrl="/checkout" signInForceRedirectUrl="/checkout" signInFallbackRedirectUrl="/checkout" appearance={clerkAppearance} /></section><p className="checkout-secure-note">Sign up, link your Discord, &amp; set up your notifications.</p></div>;
}

function CheckoutFrame({ children }: { children: ReactNode }) {
  return <div className="checkout-page"><header className="simple-header checkout-header"><a className="simple-brand" href="/" aria-label="DropDex Alerts home"><img src={logo} alt="" /><span>DropDex Alerts</span></a><a href={authUrl("/sign-in", "/checkout")}>Sign in</a></header><main className="checkout-page-main">{children}</main><SimpleFooter /></div>;
}

function CheckoutPage() {
  if (!clerkConfigured) return <CheckoutFrame><div className="center-card"><Badge tone="muted">Configuration required</Badge><h1>Checkout is unavailable</h1><p>Add the Clerk publishable key before enabling purchases.</p></div></CheckoutFrame>;
  return <CheckoutFrame><Show when="signed-in" fallback={<CheckoutAccountStep />}><CheckoutMember /></Show></CheckoutFrame>;
}

function CheckoutSuccessPage() {
  return <PageFrame><div className="center-card wide"><Badge tone="green">Free trial active</Badge><h1>Free trial started</h1><p>You can cancel at any time during the free trial if you are not satisfied.</p><div className="action-row"><a className="button button-gold success-dashboard-button" href="/account/billing">Go to dashboard</a></div></div></PageFrame>;
}

function PurchasePage({ path }: { path: string }) {
  const isDownload = path.startsWith("/download");
  if (path === "/checkout") return <CheckoutPage />;
  if (path === "/checkout/success") return <CheckoutSuccessPage />;
  const title = isDownload ? (path.endsWith("windows") ? "Download for Windows" : path.endsWith("macos") ? "Download for macOS" : "Download DropDex Alerts") : "Checkout canceled";
  return <PageFrame><div className="center-card wide"><Badge tone="gold">{isDownload ? "Desktop app" : "No charge"}</Badge><h1>{title}</h1><p>{isDownload ? "Signed installers and release metadata will appear here when desktop distribution is configured." : "Nothing was charged. You can return home whenever you’re ready."}</p><div className="action-row">{isDownload ? <><Button>Windows installer (coming soon)</Button><Button variant="ghost">macOS installer (coming soon)</Button></> : <a className="button button-gold" href="/">Return home</a>}</div></div></PageFrame>;
}

function HomeRedirect() {
  useEffect(() => window.location.replace("/"), []);
  return null;
}

const accountLabels: Record<string, [string, string]> = {
  "/account/billing": ["Membership & billing", "Manage your plan, payment method, receipts, renewal, and cancellation."],
  "/account/discord": ["Discord access", "Connect the Discord account that should receive your DropDex member role."],
  "/account/notifications": ["Discord notifications", "Choose the DropDex alerts and Discord push notifications you want to receive."]
};

function AccountPage({ path }: { path: keyof typeof accountLabels }) {
  const [title, copy] = accountLabels[path];
  return <PageFrame account><div className="account-shell"><div className="page-title compact"><Badge tone="muted">DropDex account</Badge><h1>{title}</h1><p>{copy}</p></div><nav className="account-tabs" aria-label="Account navigation">{accountRoutes.map((route) => <a className={route === path ? "active" : ""} href={route} key={route}>{accountLabels[route]?.[0]}</a>)}</nav><main><GenericAccountContent path={path} /></main></div></PageFrame>;
}

function ProtectedAccountPage({ path }: { path: keyof typeof accountLabels }) {
  if (!clerkConfigured) {
    return <PageFrame><div className="center-card"><Badge tone="muted">Configuration required</Badge><h1>Account access is unavailable</h1><p>Add the Clerk publishable key to the web environment, then redeploy the site.</p></div></PageFrame>;
  }
  return <Show when="signed-in" fallback={<PageFrame><div className="center-card"><Badge tone="gold">Members only</Badge><h1>Sign in to your account</h1><p>Your DropDex membership and billing details are protected by your account session.</p><a className="button button-gold" href={`/sign-in?redirect_url=${encodeURIComponent(path)}`}>Sign in</a></div></PageFrame>}><AccountPage path={path} /></Show>;
}

function displayBillingDate(value: string | null | undefined) {
  if (!value) return "Not available";
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value) ? `${value.replace(" ", "T")}Z` : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? "Not available" : new Intl.DateTimeFormat(undefined, { month: "long", day: "numeric", year: "numeric" }).format(date);
}

function membershipLabel(billing: BillingSummary | null) {
  if (!billing?.subscription) return "No membership";
  if (billing.subscription.status === "trialing") return "Free trial active";
  if (billing.subscription.status === "active") return billing.subscription.cancelAtPeriodEnd ? "Active · Cancels at period end" : "Active membership";
  if (billing.subscription.status === "past_due") return "Payment needs attention";
  return billing.subscription.status.replaceAll("_", " ");
}

function BillingContent() {
  const { billing, state, apiUrl, getToken } = useBillingStatus();
  const [action, setAction] = useState<"ready" | "opening" | "error">("ready");
  async function openBilling() {
    try {
      setAction("opening");
      await openBillingDestination(apiUrl, getToken, billing?.subscription ? "portal" : "checkout");
    } catch {
      setAction("error");
    }
  }
  const subscription = billing?.subscription;
  const dateLabel = subscription?.status === "trialing" ? "Trial ends" : subscription?.cancelAtPeriodEnd ? "Access ends" : "Next renewal";
  const dateValue = subscription?.status === "trialing" ? subscription.trialEnd : subscription?.currentPeriodEnd;
  return <Panel title="DropDex membership" className="membership-panel"><div className="membership-panel-body"><div className="subscription-summary"><div><small>Status</small><strong>{state === "loading" ? "Checking…" : state === "error" ? "Unable to verify" : membershipLabel(billing)}</strong></div><div><small>Price</small><strong>$5/month</strong></div><div><small>{dateLabel}</small><strong>{displayBillingDate(dateValue)}</strong></div><div><small>Discord</small><strong>Full access</strong></div></div><div className="billing-controls"><div><Badge tone={subscription ? "green" : "muted"}>{subscription ? "Membership active" : "Membership required"}</Badge><h2>{subscription ? "Manage billing" : "Start your 7-day free trial"}</h2><p>{subscription ? "Update your payment method, view receipts, review renewal details, or cancel anytime through Stripe." : "Start your membership to unlock the DropDex Discord community and alerts."}</p></div><Button disabled={state !== "ready" || action === "opening"} onClick={() => void openBilling()}>{action === "opening" ? "Opening Stripe…" : subscription ? "Manage billing" : "Start free trial"}</Button></div>{action === "error" && <p className="billing-error">Unable to open billing. Refresh the page and try again.</p>}</div></Panel>;
}

type DiscordConnectionSummary = {
  configured: boolean;
  configurationError: string | null;
  connected: boolean;
  accessGranted: boolean;
  reconnectRequired: boolean;
  servers: Array<{
    key: string;
    name: string;
    joined: boolean | null;
    memberRoleGranted: boolean;
    error: string | null;
  }>;
  discordUser: null | { id: string; username: string; displayName: string; avatarUrl: string | null };
  linkedAt: string | null;
  lastSyncedAt: string | null;
  lastError: string | null;
};

function DiscordContent() {
  const { getToken } = useAuth();
  const apiUrl = apiBaseUrl;
  const [connection, setConnection] = useState<DiscordConnectionSummary | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [action, setAction] = useState<"ready" | "connecting" | "syncing" | "disconnecting" | "error">("ready");

  useEffect(() => {
    let active = true;
    async function loadDiscord() {
      try {
        const token = await getToken();
        if (!token || !apiUrl) throw new Error("Missing Discord account configuration");
        const response = await fetch(`${apiUrl}/api/v1/discord`, { headers: { authorization: `Bearer ${token}` } });
        const payload = await response.json() as { data?: DiscordConnectionSummary };
        if (!response.ok || !payload.data) throw new Error("Unable to load Discord access");
        if (active) {
          setConnection(payload.data);
          setState("ready");
        }
      } catch {
        if (active) setState("error");
      }
    }
    void loadDiscord();
    return () => { active = false; };
  }, [apiUrl, getToken]);

  async function runDiscordAction(kind: "connect" | "sync" | "disconnect") {
    try {
      setAction(kind === "connect" ? "connecting" : kind === "sync" ? "syncing" : "disconnecting");
      const token = await getToken();
      if (!token || !apiUrl) throw new Error("Missing Discord account configuration");
      const response = await fetch(`${apiUrl}/api/v1/discord${kind === "connect" ? "/connect" : kind === "sync" ? "/sync" : ""}`, {
        method: kind === "disconnect" ? "DELETE" : "POST",
        headers: { authorization: `Bearer ${token}` }
      });
      const payload = response.status === 204
        ? null
        : await response.json() as { data?: DiscordConnectionSummary | { authorizationUrl?: string }; error?: { message?: string } };
      if (!response.ok) throw new Error(payload?.error?.message || "Discord access could not be updated.");
      if (kind === "connect") {
        const authorizationUrl = (payload?.data as { authorizationUrl?: string } | undefined)?.authorizationUrl;
        if (!authorizationUrl) throw new Error("Discord did not return an authorization URL.");
        window.location.assign(authorizationUrl);
        return;
      }
      if (kind === "disconnect") {
        setConnection((current) => current ? { ...current, connected: false, accessGranted: false, discordUser: null } : current);
      } else if (payload?.data) {
        setConnection(payload.data as DiscordConnectionSummary);
      }
      setAction("ready");
    } catch {
      setAction("error");
    }
  }

  const connected = Boolean(connection?.connected);
  const roleActive = Boolean(connection?.accessGranted);
  const serverCount = connection?.servers.length || 0;
  const reconnectRequired = Boolean(connection?.reconnectRequired);
  return <Panel title="Discord member access"><div className="billing-callout"><Badge tone={roleActive ? "green" : connected ? "gold" : "muted"}>{state === "loading" ? "Checking Discord…" : roleActive ? `${serverCount} ${serverCount === 1 ? "server" : "servers"} active` : connected ? "Discord connected" : "Discord not connected"}</Badge><h2>{roleActive ? `Connected as ${connection?.discordUser?.displayName || connection?.discordUser?.username || "Discord member"}` : reconnectRequired ? "Reconnect to join every DropDex server" : connected ? "Sync your DropDex member roles" : "Connect Discord to unlock member channels"}</h2><p>{roleActive ? "The DropDex bot is keeping your premium roles synchronized with your membership across every included Discord server." : reconnectRequired ? "Your Discord identity is linked, but one or more DropDex servers still need your authorization." : connected ? "Your Discord account is linked, but every member role is not active yet. Sync again after confirming your membership." : "Authorize the Discord account you use for DropDex. An active membership is required before the bot grants access."}</p>{connected && connection?.servers.length ? <ul className="discord-server-status">{connection.servers.map((server) => <li key={server.key}><span>{server.name}</span><strong className={server.memberRoleGranted ? "active" : ""}>{server.memberRoleGranted ? "Active" : server.joined === false ? "Reconnect needed" : server.joined === null ? "Unavailable" : "Role not active"}</strong></li>)}</ul> : null}</div>{state === "error" ? <p className="billing-error">Discord access could not be loaded. Refresh the page and try again.</p> : <div className="action-row">{connected ? <>{reconnectRequired ? <Button disabled={action !== "ready"} onClick={() => void runDiscordAction("connect")}>{action === "connecting" ? "Opening Discord…" : "Reconnect Discord"}</Button> : <Button disabled={action !== "ready"} onClick={() => void runDiscordAction("sync")}>{action === "syncing" ? "Syncing roles…" : "Sync member roles"}</Button>}<Button variant="ghost" disabled={action !== "ready"} onClick={() => void runDiscordAction("disconnect")}>{action === "disconnecting" ? "Disconnecting…" : "Disconnect Discord"}</Button></> : <Button disabled={state !== "ready" || action !== "ready" || !connection?.configured} onClick={() => void runDiscordAction("connect")}>{action === "connecting" ? "Opening Discord…" : connection?.configured ? "Connect Discord" : "Discord setup unavailable"}</Button>}</div>}{action === "error" && <p className="billing-error">Discord could not be updated. Confirm your membership is active, then try again.</p>}{connection?.lastError && <p className="billing-error">{connection.lastError}</p>}{connection?.configurationError && <p className="billing-error">{connection.configurationError}</p>}</Panel>;
}

type DiscordNotificationSettings = {
  configured: boolean;
  configurationError: string | null;
  connected: boolean;
  memberAccess: boolean;
  reconnectRequired: boolean;
  guilds: Array<{
    key: string;
    name: string;
    joined: boolean | null;
    memberRoleGranted: boolean;
    available: boolean;
    error: string | null;
    roles: Array<{
      key: string;
      name: string;
      description: string | null;
      enabled: boolean;
      selected: boolean;
    }>;
  }>;
};

function DiscordNotificationsContent() {
  const { getToken } = useAuth();
  const apiUrl = apiBaseUrl;
  const [settings, setSettings] = useState<DiscordNotificationSettings | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [connecting, setConnecting] = useState(false);
  const [updating, setUpdating] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    const token = await getToken();
    if (!token || !apiUrl) throw new Error("Discord notification settings are unavailable.");
    const response = await fetch(`${apiUrl}/api/v1/discord/notifications`, {
      headers: { authorization: `Bearer ${token}` },
      cache: "no-store"
    });
    const payload = await response.json() as { data?: DiscordNotificationSettings; error?: { message?: string } };
    if (!response.ok || !payload.data) throw new Error(payload.error?.message || "Discord notification settings could not be loaded.");
    return payload.data;
  }, [apiUrl, getToken]);

  useEffect(() => {
    let active = true;
    void loadSettings()
      .then((data) => {
        if (!active) return;
        setSettings(data);
        setState("ready");
      })
      .catch(() => {
        if (active) setState("error");
      });
    return () => { active = false; };
  }, [loadSettings]);

  async function connectDiscord() {
    try {
      setConnecting(true);
      setMessage(null);
      const token = await getToken();
      if (!token || !apiUrl) throw new Error("Discord connection is unavailable.");
      const response = await fetch(`${apiUrl}/api/v1/discord/connect`, {
        method: "POST",
        headers: { authorization: `Bearer ${token}` }
      });
      const payload = await response.json() as { data?: { url?: string }; error?: { message?: string } };
      if (!response.ok || !payload.data?.url) throw new Error(payload.error?.message || "Discord authorization could not be opened.");
      window.location.assign(payload.data.url);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "Discord authorization could not be opened.");
      setConnecting(false);
    }
  }

  async function updateRole(guildKey: string, roleKey: string, enabled: boolean) {
    const updateKey = `${guildKey}:${roleKey}`;
    try {
      setUpdating(updateKey);
      setMessage(null);
      const token = await getToken();
      if (!token || !apiUrl) throw new Error("Discord notification settings are unavailable.");
      const response = await fetch(`${apiUrl}/api/v1/discord/notifications`, {
        method: "PUT",
        headers: {
          authorization: `Bearer ${token}`,
          "content-type": "application/json"
        },
        body: JSON.stringify({ guildKey, roleKey, enabled })
      });
      const payload = await response.json() as { data?: DiscordNotificationSettings; error?: { message?: string } };
      if (!response.ok || !payload.data) throw new Error(payload.error?.message || "That Discord role could not be updated.");
      setSettings(payload.data);
      setMessage(enabled ? "Notification role added." : "Notification role removed.");
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "That Discord role could not be updated.");
    } finally {
      setUpdating(null);
    }
  }

  if (state === "loading") {
    return <Panel title="Discord notification settings" className="discord-notifications-panel"><div className="discord-notifications-body"><Badge tone="muted">Loading</Badge><h2>Checking your Discord roles…</h2></div></Panel>;
  }
  if (state === "error" || !settings) {
    return <Panel title="Discord notification settings" className="discord-notifications-panel"><div className="discord-notifications-body"><Badge tone="muted">Unavailable</Badge><h2>Notification settings could not be loaded</h2><p>Refresh the page and try again.</p></div></Panel>;
  }
  if (!settings.connected) {
    return <Panel title="Discord notification settings" className="discord-notifications-panel"><div className="discord-notifications-body"><Badge tone="gold">Discord required</Badge><h2>Connect Discord first</h2><p>Link the Discord account you use in the DropDex community before choosing notification roles.</p><button type="button" className="button button-gold notification-connect-button" disabled={connecting} onClick={() => void connectDiscord()}>{connecting ? "Opening Discord…" : "Connect Discord"}</button>{message && <p className="billing-error" aria-live="polite">{message}</p>}</div></Panel>;
  }

  return <Panel title="Discord notification settings" className="discord-notifications-panel">
    <div className="discord-notifications-body">
      <Badge tone={settings.memberAccess ? "green" : "muted"}>{settings.memberAccess ? "Membership active" : "Membership required"}</Badge>
      <h2>Choose your Discord alerts</h2>
      <p>Each Discord has its own notification roles. Turn alerts on or off separately for each community below.</p>
      {settings.reconnectRequired && <div className="notification-warning"><strong>Reconnect Discord to finish setup</strong><span>One of your included DropDex servers has not been joined yet.</span><a href="/account/discord">Reconnect Discord</a></div>}
      {settings.guilds.length ? <div className="discord-role-guilds">{settings.guilds.map((guild, index) => {
        const status = guild.joined === false ? "Reconnect required" : guild.joined === null ? "Server unavailable" : guild.memberRoleGranted ? "Member access active" : "Member role not active";
        return <section className="discord-role-guild" key={guild.key}>
          <header>
            <div>
              <span className="discord-guild-label">{index === 0 ? "Main Discord" : "All Sites Discord"}</span>
              <h3>{guild.name}</h3>
              <small>{status}</small>
            </div>
            <Badge tone={guild.memberRoleGranted ? "green" : "muted"}>{guild.memberRoleGranted ? "Connected" : "Needs attention"}</Badge>
          </header>
          {guild.roles.length ? <div className="discord-role-list">{guild.roles.map((role) => {
            const key = `${guild.key}:${role.key}`;
            const disabled = !settings.memberAccess || !guild.available || updating !== null;
            return <div className="discord-role-row" key={role.key}>
              <div><strong>{role.name}</strong>{role.description && <small>{role.description}</small>}</div>
              <button type="button" className={`role-switch ${role.enabled ? "enabled" : ""}`} role="switch" aria-checked={role.enabled} aria-label={`${role.enabled ? "Remove" : "Add"} ${role.name} role`} disabled={disabled} onClick={() => void updateRole(guild.key, role.key, !role.enabled)}><span aria-hidden="true" />{updating === key ? "Saving…" : role.enabled ? "On" : "Off"}</button>
            </div>;
          })}</div> : <p className="empty-role-copy">Notification role IDs are needed for this Discord before choices can appear.</p>}
        </section>;
      })}</div> : <div className="notification-warning"><strong>Discord servers are being configured</strong><span>Your notification choices will appear here after server setup is completed.</span></div>}
      {!settings.memberAccess && <p className="billing-error">An active membership is required to change Discord roles.</p>}
      {settings.configurationError && <p className="billing-error">{settings.configurationError}</p>}
      {message && <p className={message.includes("added") || message.includes("removed") ? "notification-success" : "billing-error"} aria-live="polite">{message}</p>}
    </div>
  </Panel>;
}

function GenericAccountContent({ path }: { path: string }) {
  if (path === "/account/discord") return <DiscordContent />;
  if (path === "/account/notifications") return <DiscordNotificationsContent />;
  return <BillingContent />;
}

function PrivacyPolicy() {
  const supportEmail = "support@dropdexalerts.com";

  return <PageFrame><article className="legal">
    <Badge tone="muted">Legal</Badge>
    <h1>Privacy Policy</h1>
    <p className="legal-updated">Last updated: August 22, 2026</p>

    <div className="legal-intro">
      <p>This Privacy Policy explains how <strong>DropDex Alerts LLC</strong> ("DropDex," "we," "us," or "our") collects, uses, shares, and protects information when you use our website, DropDex account and membership, Discord communities, alerts, tools, customer support, and related services (collectively, the "Service").</p>
      <p>By using the Service, you acknowledge the practices described in this Policy.</p>
    </div>

    <div className="legal-content">
      <section>
        <h2>1. Who We Are</h2>
        <p>DropDex Alerts LLC operates an independent trading-card and collector alerting community. We are not affiliated with or endorsed by the retailers, marketplaces, card-game publishers, or product manufacturers referenced through the Service.</p>
        <p>Questions or privacy requests may be sent to <a className="gold" href={`mailto:${supportEmail}`}>{supportEmail}</a>.</p>
      </section>

      <section>
        <h2>2. Information We Collect</h2>
        <p>We collect information you provide, information generated when you use the Service, and limited information received from service providers.</p>

        <h3>Account and identity information</h3>
        <p>When you create or access a DropDex account, we may collect your name, email address, account identifier, profile information, authentication status, and other information you choose to provide. Clerk provides our account authentication and identity services.</p>

        <h3>Membership and subscription information</h3>
        <p>We may receive membership details from Stripe, including your Stripe customer and subscription identifiers, selected plan, subscription status, trial dates, renewal date, cancellation status, and billing-management information. Stripe processes checkout and payment information. DropDex does not receive or store your full payment-card number.</p>

        <h3>Discord and community information</h3>
        <p>When you connect your Discord account or participate in a DropDex Discord community, we may process your Discord username, Discord user ID, server membership, membership and notification roles, messages, reactions, support requests, alert preferences, and other content or activity you choose to provide.</p>

        <h3>Support and communications</h3>
        <p>If you contact us through email, Discord, or another support channel, we may collect your contact information, messages, attachments, conversation history, and information needed to investigate or resolve your request.</p>

        <h3>Usage, device, and technical information</h3>
        <p>We and our service providers may automatically receive limited technical information such as your IP address, browser type, device type, operating system, referring page, pages visited, access times, approximate location derived from your IP address, diagnostic information, and security logs.</p>

        <h3>Preferences</h3>
        <p>We may collect the games, products, retailers, regions, keywords, Discord roles, channels, or alert categories you choose to follow so that we can provide relevant alerts and community features.</p>
      </section>

      <section>
        <h2>3. Cookies and Similar Technologies</h2>
        <p>The website uses cookies and similar technologies that are necessary for sign-in, security, checkout, billing, and basic site operation. These may include:</p>
        <ul>
          <li>Clerk technologies used to authenticate your account and keep you signed in</li>
          <li>Secure session and anti-fraud technologies used to protect account access</li>
          <li>Stripe technologies used to provide checkout, subscriptions, and billing management</li>
          <li>Cloudflare technologies used to deliver, monitor, and protect the website and related services</li>
        </ul>
        <p>You can control cookies through your browser settings. Blocking essential cookies may prevent sign-in, checkout, membership management, or other Service features from working correctly.</p>
        <p>DropDex does not currently use personal information for cross-context behavioral advertising and does not sell personal information.</p>
      </section>

      <section>
        <h2>4. How We Use Information</h2>
        <p>We may use information to:</p>
        <ul>
          <li>Create, authenticate, and maintain your DropDex account</li>
          <li>Verify subscription status and provide trial or paid membership features</li>
          <li>Connect your Discord account and manage included server access and roles</li>
          <li>Provide alerts, tools, community access, and preferences you select</li>
          <li>Process and support subscriptions through Stripe</li>
          <li>Respond to questions and provide customer support</li>
          <li>Operate, maintain, troubleshoot, secure, and improve the Service</li>
          <li>Detect fraud, abuse, unauthorized access, and violations of our Terms</li>
          <li>Send service-related messages about your account, membership, security, or material policy changes</li>
          <li>Comply with legal, accounting, and regulatory obligations</li>
          <li>Establish, exercise, or defend legal claims</li>
        </ul>
      </section>

      <section>
        <h2>5. Legal Bases for Processing</h2>
        <p>Where applicable law requires a legal basis, we process personal information:</p>
        <ul>
          <li>To perform our contract with you, including providing your membership and requested features</li>
          <li>For legitimate interests such as security, fraud prevention, support, and improving the Service</li>
          <li>With your consent when consent is required</li>
          <li>To comply with legal obligations</li>
        </ul>
        <p>Where processing is based on consent, you may withdraw that consent at any time. Withdrawal does not affect processing that occurred before consent was withdrawn.</p>
      </section>

      <section>
        <h2>6. How We Share Information</h2>
        <p>We do not sell your personal information. We may share information only as reasonably necessary with:</p>
        <ul>
          <li><strong>Clerk</strong> for account creation, authentication, identity verification, and session management</li>
          <li><strong>Stripe</strong> for checkout, payment processing, subscription verification, and billing management</li>
          <li><strong>Discord</strong> for community access, server membership, roles, communications, alerts, and moderation</li>
          <li><strong>Cloudflare</strong> and other infrastructure or security providers that operate, deliver, monitor, and protect the Service</li>
          <li>Professional advisers such as attorneys, accountants, auditors, or insurers when reasonably necessary</li>
          <li>Government authorities or other parties when required by law or necessary to protect rights, safety, and security</li>
        </ul>
        <p>If DropDex is involved in a merger, acquisition, financing, reorganization, sale of assets, or similar business transaction, information may be disclosed as part of that transaction, subject to applicable law.</p>
      </section>

      <section>
        <h2>7. Third-Party Services</h2>
        <p>Clerk, Stripe, Discord, Cloudflare, retailers, marketplaces, social-media platforms, and other linked services operate under their own privacy policies. This Policy does not govern information those third parties collect independently from you.</p>
        <p>We encourage you to review a third party's privacy practices before providing information or using its services.</p>
      </section>

      <section>
        <h2>8. Data Retention</h2>
        <p>We retain personal information for as long as reasonably necessary to provide the Service, maintain your account and membership, resolve disputes, prevent fraud, enforce our agreements, and comply with legal, tax, accounting, or regulatory obligations.</p>
        <p>Retention periods may vary depending on the type of information, the purpose for which it was collected, and applicable legal requirements. When information is no longer needed, we may delete or de-identify it.</p>
        <p>Information retained independently by Clerk, Stripe, Discord, Cloudflare, or another third party is governed by that provider's retention practices.</p>
      </section>

      <section>
        <h2>9. Data Security</h2>
        <p>We use reasonable administrative, technical, and organizational measures designed to protect personal information. These measures include encrypted connections, protected sign-in sessions, restricted access, and service providers that support secure authentication, payments, hosting, and communications.</p>
        <p>No online service or method of storage is completely secure. We cannot guarantee that information will never be accessed, disclosed, altered, or destroyed without authorization.</p>
      </section>

      <section>
        <h2>10. Your Rights and Choices</h2>
        <p>Depending on where you live, you may have the right to request access to, correction of, deletion of, or a portable copy of your personal information. You may also have the right to object to or restrict certain processing, withdraw consent, or appeal a decision concerning a privacy request.</p>
        <ul>
          <li><strong>Account and data requests:</strong> Email <a className="gold" href={`mailto:${supportEmail}`}>{supportEmail}</a>.</li>
          <li><strong>Subscription management:</strong> Manage or cancel your subscription through the billing controls in your DropDex account. Deleting account information does not automatically cancel a Stripe subscription.</li>
          <li><strong>Discord:</strong> Use your DropDex notification settings to manage available notification roles and use your Discord account to manage Discord privacy and device notification settings.</li>
          <li><strong>Support:</strong> You may ask us to close a support conversation or delete eligible support information.</li>
          <li><strong>Cookies:</strong> Control cookies through your browser settings, subject to the functional limitations described above.</li>
        </ul>
        <p>We may need to verify your identity before completing a request. We will not discriminate against you for exercising an applicable privacy right.</p>
      </section>

      <section>
        <h2>11. California Privacy Notice</h2>
        <p>California residents may have rights to know the categories and specific pieces of personal information collected, request deletion or correction, obtain information about disclosures, and opt out of certain sales or sharing.</p>
        <p>DropDex does not sell personal information and does not currently share personal information for cross-context behavioral advertising. To submit a California privacy request, email <a className="gold" href={`mailto:${supportEmail}`}>{supportEmail}</a>.</p>
      </section>

      <section>
        <h2>12. International Users</h2>
        <p>DropDex and its service providers may process information in the United States and other countries where privacy laws may differ from those in your location.</p>
        <p>Users in the European Economic Area, United Kingdom, or Switzerland may have rights to access, correct, delete, restrict, or object to processing; request portability; withdraw consent; and complain to a local data-protection authority.</p>
      </section>

      <section>
        <h2>13. Children's Privacy</h2>
        <p>The Service is not directed to children under 13, and we do not knowingly collect personal information from children under 13. If you believe a child under 13 has provided personal information, contact us at <a className="gold" href={`mailto:${supportEmail}`}>{supportEmail}</a> so we can investigate and delete the information where appropriate.</p>
      </section>

      <section>
        <h2>14. Changes to This Policy</h2>
        <p>We may update this Privacy Policy periodically. When we do, we will revise the "Last updated" date and may provide additional notice when required by law. Your continued use of the Service after an updated Policy becomes effective means you acknowledge the revised Policy.</p>
      </section>

      <section>
        <h2>15. Contact Us</h2>
        <p>Questions, concerns, or privacy requests may be sent to:</p>
        <address><strong>DropDex Alerts LLC</strong><br />Email: <a className="gold" href={`mailto:${supportEmail}`}>{supportEmail}</a></address>
      </section>
    </div>
  </article></PageFrame>;
}

function TermsOfService() {
  const supportEmail = "support@dropdexalerts.com";

  return <PageFrame><article className="legal">
    <Badge tone="muted">Legal</Badge>
    <h1>Terms of Service</h1>
    <p className="legal-updated">Last updated: August 22, 2026</p>

    <div className="legal-intro">
      <p>These Terms of Service ("Terms") govern your access to and use of the DropDex Alerts website, DropDex account and membership, Discord communities, alerts, tools, content, and related features (collectively, the "Service").</p>
      <p>The Service is operated by <strong>DropDex Alerts LLC</strong> ("DropDex," "we," "us," or "our"). By creating an account, starting a free trial, purchasing a membership, joining a DropDex Discord community, or otherwise using the Service, you agree to these Terms. If you do not agree, you may not use the Service.</p>
    </div>

    <div className="legal-content">
      <section>
        <h2>1. The DropDex Service</h2>
        <p>DropDex is an independent collector community and information service that may provide:</p>
        <ul>
          <li>Trading-card and collectible restock alerts</li>
          <li>Product-release and preorder notifications</li>
          <li>Retail and online inventory monitoring</li>
          <li>Deals and potential resale-opportunity alerts</li>
          <li>Market updates and collector news</li>
          <li>Discord community channels, roles, and notification preferences</li>
          <li>Comics, collectibles, and other product-related information</li>
        </ul>
        <p>DropDex does not manufacture, distribute, or sell products included in its alerts unless expressly stated otherwise. DropDex is not affiliated with, endorsed by, or sponsored by Pokémon, The Pokémon Company, Nintendo, Bandai, Disney, Topps, Fanatics, any retailer, card-game publisher, marketplace, or other third party referenced through the Service. All trademarks and product names belong to their respective owners.</p>
      </section>

      <section>
        <h2>2. No Guarantee of Availability or Profit</h2>
        <p>DropDex provides information and alerts only. We do not guarantee:</p>
        <ul>
          <li>That an alert or Discord push notification will be delivered immediately</li>
          <li>That alert information will always be complete or accurate</li>
          <li>That a product will remain available after an alert is delivered</li>
          <li>That inventory information will match a retailer's actual inventory</li>
          <li>That you will successfully purchase or reserve a product</li>
          <li>That a retailer will honor an order or listed price</li>
          <li>That any product will increase in value</li>
          <li>That any purchase, resale, deal, or opportunity will generate a profit</li>
        </ul>
        <p>Products may sell out, orders may be canceled, prices may change, and retailers may limit or reject purchases. You are solely responsible for deciding whether to purchase, hold, sell, or otherwise transact in any product.</p>
      </section>

      <section>
        <h2>3. Eligibility</h2>
        <p>You must be at least 13 years old to access the Service. If you are under 18 or have not reached the age of legal majority where you live, you may use the Service only with the permission and supervision of a parent or legal guardian. The parent or legal guardian is responsible for your use of the Service and any payments made through your account.</p>
        <p>You may not use the Service if doing so would violate applicable law.</p>
      </section>

      <section>
        <h2>4. Accounts and Discord Access</h2>
        <p>Certain features require a DropDex account, Discord account, or another third-party account. You agree to:</p>
        <ul>
          <li>Provide accurate and current account information</li>
          <li>Keep your login credentials secure</li>
          <li>Maintain access to the DropDex and Discord accounts connected to your membership</li>
          <li>Accept responsibility for activity occurring through your accounts</li>
          <li>Notify us promptly if you believe your account has been compromised</li>
        </ul>
        <p>Membership access is intended for the individual purchaser. You may not share, sell, transfer, or provide your account or membership access to another person without written permission from DropDex.</p>
        <p>We are not responsible for account restrictions, outages, or actions imposed by Clerk, Stripe, Discord, retailers, or other third-party platforms.</p>
      </section>

      <section>
        <h2>5. Subscriptions, Free Trials, and Billing</h2>
        <p>DropDex offers an automatically renewing paid membership. Current pricing, trial terms, included features, billing frequency, and applicable taxes are displayed before checkout. Unless otherwise stated at checkout, the DropDex Alerts membership includes a <strong>7-day free trial</strong> followed by a recurring charge of <strong>$5 per month</strong>.</p>
        <p>Payments and subscription management are processed through Stripe or another authorized payment provider. By starting a free trial or purchasing a membership, you authorize the applicable payment provider to charge your selected payment method when the trial ends and at the beginning of each renewal period until you cancel.</p>

        <h3>Free trials</h3>
        <p>Eligibility for a free trial may be limited to new members. Unless you cancel before the trial expires, your membership will automatically convert into a paid subscription at the price shown during checkout. Creating multiple accounts to receive additional free trials is prohibited.</p>

        <h3>Automatic renewal</h3>
        <p>Your membership automatically renews at the frequency and price disclosed during checkout unless you cancel before the next billing date. If pricing changes, we will provide notice where required by law.</p>

        <h3>Cancellation</h3>
        <p>You may cancel through the membership and billing controls in your DropDex account or another cancellation method we make available. Cancellation prevents future renewals. Unless otherwise required by law, you will retain access through the end of your current paid billing period.</p>

        <h3>Refunds</h3>
        <p>Except where required by law or expressly stated otherwise, subscription payments are nonrefundable. We do not provide refunds or credits for partially used billing periods, missed alerts, unsuccessful purchases, retailer cancellations, or a failure to use the Service. Additional details appear in our <a className="gold" href="/refund-policy">Refund Policy</a>.</p>
      </section>

      <section>
        <h2>6. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Service to violate any law, regulation, retailer policy, or third-party terms</li>
          <li>Share, leak, copy, resell, sublicense, or redistribute DropDex alerts, monitors, guides, tools, data, or paid content</li>
          <li>Use unauthorized bots, scripts, scraping tools, crawlers, or automated methods to access or extract information from the Service</li>
          <li>Attempt to reverse engineer, bypass, disable, or interfere with any part of the Service</li>
          <li>Attempt to gain unauthorized access to another account, system, server, or network</li>
          <li>Circumvent membership restrictions, access controls, or security measures</li>
          <li>Abuse free trials, promotions, referrals, refunds, or payment disputes</li>
          <li>Engage in fraud, chargeback abuse, deceptive conduct, or payment activity involving stolen or unauthorized payment methods</li>
          <li>Harass, threaten, impersonate, discriminate against, or abuse another community member</li>
          <li>Post unlawful, infringing, misleading, sexually explicit, hateful, or malicious content</li>
          <li>Distribute malware, phishing links, spam, or harmful code</li>
          <li>Use the Service in a way that harms DropDex, its members, retailers, or third parties</li>
        </ul>
        <p>We may remove content or restrict access when we reasonably believe these rules have been violated.</p>
      </section>

      <section>
        <h2>7. Community Conduct</h2>
        <p>Access to DropDex Discord communities is a membership benefit and may be subject to additional community rules. You are responsible for content you post or share and represent that you have the right to share it.</p>
        <p>You grant DropDex a nonexclusive, worldwide, royalty-free license to display, reproduce, and share content you voluntarily submit for community operation, moderation, support, or promotional purposes. We will seek permission before using personally identifiable member-success content in public advertising when reasonably appropriate.</p>
        <p>We may moderate, restrict, or remove community content at our discretion to protect members and maintain the Service.</p>
      </section>

      <section>
        <h2>8. Intellectual Property</h2>
        <p>The Service—including its branding, logo, website design, alert formats, written guides, original graphics, databases, software, tools, and other original content—is owned by DropDex Alerts LLC or its licensors and is protected by applicable intellectual-property laws.</p>
        <p>Your membership provides a limited, personal, nonexclusive, nontransferable, and revocable right to use the Service for lawful personal purposes. No ownership rights are transferred to you.</p>
      </section>

      <section>
        <h2>9. Third-Party Services and Links</h2>
        <p>The Service may contain information, integrations, embeds, or links involving Clerk, Stripe, Discord, Cloudflare, retailers, marketplaces, payment providers, and other third parties. Third-party services operate under their own terms and privacy policies.</p>
        <p>DropDex does not control and is not responsible for:</p>
        <ul>
          <li>Third-party websites or platforms</li>
          <li>Product descriptions, pricing, or inventory accuracy</li>
          <li>Retailer purchase limits</li>
          <li>Order acceptance, fulfillment, shipping, returns, or refunds</li>
          <li>Marketplace transactions or payment-provider decisions</li>
          <li>Third-party outages, account restrictions, or security incidents</li>
        </ul>
        <p>Following an alert or third-party link is at your own risk.</p>
      </section>

      <section>
        <h2>10. Service Changes and Availability</h2>
        <p>We may add, remove, modify, suspend, or discontinue features, alerts, retailers, product categories, community channels, or other parts of the Service. We do not guarantee that the Service will be uninterrupted, error-free, or available at all times.</p>
        <p>Scheduled maintenance, technical problems, retailer changes, third-party outages, rate limits, security events, or circumstances outside our control may affect the Service.</p>
      </section>

      <section>
        <h2>11. Disclaimer of Warranties</h2>
        <p>To the fullest extent permitted by law, the Service is provided <strong>"as is"</strong> and <strong>"as available."</strong></p>
        <p>DropDex disclaims all express and implied warranties, including warranties of merchantability, fitness for a particular purpose, title, accuracy, availability, and non-infringement. We do not warrant that alerts, market information, product information, inventory data, tools, links, or community content will be accurate, complete, timely, secure, or suitable for your intended purpose.</p>
        <p>Some jurisdictions do not permit certain warranty exclusions, so portions of this section may not apply to you.</p>
      </section>

      <section>
        <h2>12. Limitation of Liability</h2>
        <p>To the fullest extent permitted by law, DropDex Alerts LLC and its owners, employees, contractors, affiliates, licensors, and service providers will not be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages.</p>
        <p>This includes damages arising from missed purchases, sold-out products, canceled or unsuccessful orders, lost profits or resale opportunities, changes in product value, inaccurate or delayed alerts, retailer or marketplace actions, account restrictions, loss of data, service interruptions, or unauthorized account access.</p>
        <p>To the fullest extent permitted by law, DropDex's total liability for any claim relating to the Service will not exceed the amount you paid to DropDex during the three months immediately preceding the event giving rise to the claim. Some jurisdictions do not allow certain limitations of liability, so portions of this section may not apply to you.</p>
      </section>

      <section>
        <h2>13. Indemnification</h2>
        <p>To the fullest extent permitted by law, you agree to defend, indemnify, and hold harmless DropDex Alerts LLC and its owners, employees, contractors, affiliates, and service providers from claims, losses, liabilities, damages, and reasonable expenses arising from your misuse of the Service, violation of these Terms, violation of applicable law, infringement of another person's rights, content you submit or distribute, or transactions or disputes between you and a retailer, marketplace, or third party.</p>
      </section>

      <section>
        <h2>14. Suspension and Termination</h2>
        <p>You may stop using the Service and cancel your membership at any time. We may suspend or terminate your access, remove you from a DropDex community, or restrict features if:</p>
        <ul>
          <li>You violate these Terms or community rules</li>
          <li>Your payment fails or your membership becomes inactive</li>
          <li>You engage in fraud, abuse, harassment, or unauthorized redistribution</li>
          <li>Your activity creates risk for DropDex, its members, or third parties</li>
          <li>Suspension is necessary to protect or maintain the Service or is required by law</li>
        </ul>
        <p>Termination does not eliminate obligations or liabilities that arose before termination. Sections intended by their nature to survive termination will remain effective.</p>
      </section>

      <section>
        <h2>15. Changes to These Terms</h2>
        <p>We may update these Terms periodically. When we make changes, we will update the "Last updated" date and may provide additional notice for material changes when required by law. Your continued use of the Service after revised Terms become effective means you accept the revised Terms.</p>
      </section>

      <section>
        <h2>16. Governing Law and Disputes</h2>
        <p>These Terms are governed by the laws of the United States and the State of New York, without regard to conflict-of-law principles. Any dispute arising from these Terms or the Service will be brought in the state or federal courts with jurisdiction in New York County, New York, unless applicable consumer law requires otherwise. You and DropDex consent to the personal jurisdiction of those courts.</p>
      </section>

      <section>
        <h2>17. Severability and Entire Agreement</h2>
        <p>If any provision of these Terms is found unenforceable, the remaining provisions will remain in effect. These Terms, together with the <a className="gold" href="/privacy">Privacy Policy</a>, <a className="gold" href="/refund-policy">Refund Policy</a>, and any additional rules or policies presented through the Service, constitute the entire agreement between you and DropDex concerning the Service.</p>
        <p>Our failure to enforce a provision does not waive our right to enforce it later.</p>
      </section>

      <section>
        <h2>18. Contact Us</h2>
        <p>Questions regarding these Terms may be sent to:</p>
        <address><strong>DropDex Alerts LLC</strong><br />Email: <a className="gold" href={`mailto:${supportEmail}`}>{supportEmail}</a></address>
      </section>
    </div>
  </article></PageFrame>;
}

function RefundPolicy() {
  const supportEmail = "support@dropdexalerts.com";

  return <PageFrame><article className="legal">
    <Badge tone="muted">Legal</Badge>
    <h1>Refund Policy</h1>
    <p className="legal-updated">Last updated: August 22, 2026</p>

    <div className="legal-intro">
      <p>This Refund Policy explains the cancellation and refund rules for DropDex Alerts memberships provided by <strong>DropDex Alerts LLC</strong> ("DropDex," "we," "us," or "our").</p>
      <p>By starting a free trial or purchasing a membership, you agree to this Refund Policy and the applicable checkout terms.</p>
    </div>

    <div className="legal-content">
      <section>
        <h2>1. Free Trial</h2>
        <p>Unless otherwise stated at checkout, eligible new members receive a 7-day free trial. You may cancel at any time before the trial ends through the membership and billing controls in your DropDex account. If you cancel before the trial expires, the subscription will not convert to a paid membership.</p>
        <p>Unless you cancel before the trial ends, your membership automatically converts to a paid subscription at the price shown during checkout. Creating multiple accounts to receive additional free trials is prohibited.</p>
      </section>

      <section>
        <h2>2. Recurring Subscription Charges</h2>
        <p>Unless otherwise stated at checkout, membership renews automatically at <strong>$5 per month</strong>. Stripe processes subscription payments. You authorize Stripe to charge your selected payment method when the trial ends and at the beginning of each renewal period until you cancel.</p>
        <p>You are responsible for canceling before your next billing date if you do not want the subscription to renew.</p>
      </section>

      <section>
        <h2>3. Cancellation</h2>
        <p>You may cancel at any time through the membership and billing controls in your DropDex account or another cancellation method we make available. Cancellation prevents future renewal charges.</p>
        <p>Unless otherwise required by law, canceling a paid membership does not create a refund for the current billing period. You will retain membership access through the end of that paid billing period.</p>
      </section>

      <section>
        <h2>4. Refund Eligibility</h2>
        <p>Except where required by law or expressly approved by DropDex, subscription payments are nonrefundable. We do not provide refunds or credits for:</p>
        <ul>
          <li>Partially used billing periods</li>
          <li>Failure to cancel before a free trial or renewal date</li>
          <li>A failure to use the Service or connect a Discord account</li>
          <li>Missed, delayed, or unavailable alerts or Discord push notifications</li>
          <li>Unsuccessful product purchases, sold-out products, or changed inventory</li>
          <li>Retailer cancellations, price changes, order limits, or fulfillment decisions</li>
          <li>Third-party outages, restrictions, or account actions</li>
        </ul>
        <p>Nothing in this Policy limits any refund or cancellation right that cannot be waived under applicable law.</p>
      </section>

      <section>
        <h2>5. Incorrect or Unauthorized Charges</h2>
        <p>If you believe you were charged more than once, charged an incorrect amount, or charged without authorization, contact us promptly so we can investigate. We may ask for your DropDex account email, the charge date and amount, and other information needed to locate the Stripe transaction and verify your identity.</p>
        <p>Approved refunds are returned to the original payment method. Processing time is controlled by Stripe, your bank, and your card provider.</p>
      </section>

      <section>
        <h2>6. Service or Access Issues</h2>
        <p>If an active membership is not providing the expected DropDex or Discord access, contact support so we can investigate and restore eligible access. Temporary outages, third-party service interruptions, or isolated delivery issues do not automatically qualify for a refund.</p>
      </section>

      <section>
        <h2>7. Policy Changes</h2>
        <p>We may update this Refund Policy periodically. When we do, we will revise the "Last updated" date and may provide additional notice when required by law. Changes will not reduce any rights that cannot be waived under applicable law.</p>
      </section>

      <section>
        <h2>8. Contact Us</h2>
        <p>Questions about cancellation, billing, or a possible refund may be sent to:</p>
        <address><strong>DropDex Alerts LLC</strong><br />Email: <a className="gold" href={`mailto:${supportEmail}`}>{supportEmail}</a></address>
      </section>
    </div>
  </article></PageFrame>;
}

function LegalPage({ path }: { path: string }) {
  if (path === "/privacy") return <PrivacyPolicy />;
  if (path === "/terms") return <TermsOfService />;
  if (path === "/refund-policy") return <RefundPolicy />;
  const title = "Support";
  return <PageFrame><div className="legal"><Badge tone="muted">Draft placeholder</Badge><h1>{title}</h1><p>This page is intentionally provided as a product shell for legal and support copy. Replace it with counsel-reviewed content before launch.</p><h2>DropDex Alerts</h2><p>dropdexalerts.app is a standalone platform with its own DropDex accounts. It is separate from the DropDex Discord product and is not affiliated with any trading card game publisher or retailer.</p><h2>Contact</h2><p>Email <a className="gold" href="mailto:support@dropdexalerts.app">support@dropdexalerts.app</a>.</p>{path === "/support" && <div className="support-socials"><strong>Official DropDex social accounts</strong>{socialLinks.map((social) => <a href={social.href} target="_blank" rel="noreferrer" key={social.href}>{social.label} ↗</a>)}</div>}</div></PageFrame>;
}

function PageFrame({ children, account = false }: { children: React.ReactNode; account?: boolean }) {
  return <><SiteHeader /><main className={`inner-page ${account ? "account-page" : ""}`}>{children}</main><SimpleFooter /></>;
}

function NotFound() {
  return <PageFrame><div className="center-card"><Badge tone="muted">404</Badge><h1>Page not found</h1><p>The page you requested isn’t part of the DropDex Alerts website.</p><a className="button button-gold" href="/">Return home</a></div></PageFrame>;
}

export function WebApp() {
  const path = useMemo(() => window.location.pathname.replace(/\/$/, "") || "/", []);
  if (path === "/") return <LandingPage />;
  if (path in authRoutes) return <AuthPage route={path as keyof typeof authRoutes} />;
  if (path === "/pricing") return <HomeRedirect />;
  if (purchaseRoutes.includes(path)) return <PurchasePage path={path} />;
  if (path === "/account") return <ProtectedAccountPage path="/account/billing" />;
  if (accountRoutes.includes(path as (typeof accountRoutes)[number])) return <ProtectedAccountPage path={path as keyof typeof accountLabels} />;
  if (legalRoutes.includes(path)) return <LegalPage path={path} />;
  return <NotFound />;
}
